import { getFormattedUnit } from 'config';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import ReactHtmlParser from "react-html-parser";

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeDecimalInput = (value) => {
    const sanitized = String(value || "").replace(/[^\d.]/g, "");
    const [integerPart, decimalPart = ""] = sanitized.split(".");
    return sanitized.includes(".") ? `${integerPart}.${decimalPart.slice(0, 2)}` : integerPart;
};

const roundQuantity = (value) => {
    const parsed = toNumber(value);
    return Number(parsed.toFixed(2));
};

const formatQuantity = (value) => {
    const rounded = roundQuantity(value);
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
};

const formatQuantityInputValue = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const textValue = String(value);
    return textValue.endsWith(".") ? textValue : formatQuantity(value);
};

const plainText = (value, fallback = "scope") => {
    const text = String(value || "")
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/gi, " ")
        .trim();
    return text || fallback;
};

const hasScopeMaterials = (scope) => Array.isArray(scope?.materials) && scope.materials.length > 0;

const getScopeUnitRate = (scope) => {
    const quantity = toNumber(scope?.quantity);
    const total = toNumber(scope?.totalExclGst);
    return quantity > 0 ? total / quantity : total;
};

const formatAmount = (value) =>
    new Intl.NumberFormat("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(toNumber(value));

const formatRevisionDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("en-GB");
};

const getDifferencePercent = (firstRate, diff) => {
    const numericFirstRate = Number(firstRate);
    const numericDiff = Number(diff);

    if (!Number.isFinite(numericFirstRate) || numericFirstRate === 0 || !Number.isFinite(numericDiff)) {
        return null;
    }

    return (Math.abs(numericDiff) / Math.abs(numericFirstRate)) * 100;
};

const ContractorRFQTable = ({
    rfqData,
    sortedSuppliers,
    handleSectionClick,
    selectedMaterialsMap,
    setSelectedMaterialsMap,
    setSelectedVendors,
    selectedRatesTab = false,
    isEditRatesLocked = false,
    showSelectedValueColumn = false,
    canSelectRates = false,
    hasBudgetAmountColumn = true,
    hiddenLineItemKeys = new Set(),
    getLineItemKey,
    renderLineItemHideButton
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [collapsedSections, setCollapsedSections] = useState({});
    const [expandedMaterialScopes, setExpandedMaterialScopes] = useState({});
    const [openRevisionTrail, setOpenRevisionTrail] = useState(() => new Set());
    const isSelectedValueVisible = canSelectRates && showSelectedValueColumn;

    const renderDifferenceValue = (safeDiff, firstRate, extraClass = "") => {
        const differencePercent = getDifferencePercent(firstRate, safeDiff);

        return (
            <div className={`flex flex-col items-end text-right font-medium ${extraClass} ${safeDiff === 0 ? "text-gray-500" : safeDiff > 0 ? "text-green-600" : "text-red-600"}`}>
                <span>
                    {safeDiff === 0 ? "0" : (
                        <>
                            {formatAmount(Math.abs(safeDiff))}
                            {safeDiff > 0 && " ▼"}
                            {safeDiff < 0 && " ▲"}
                        </>
                    )}
                </span>
                <span className="text-[10px] leading-3">
                    {differencePercent === null ? "-" : `(${differencePercent.toFixed(2)}%)`}
                </span>
            </div>
        );
    };

    const formatRevisionDifference = (difference, differencePercent) => {
        if (difference === null || difference === undefined) return "-";

        const numericDifference = Number(difference);
        if (!Number.isFinite(numericDifference)) return "-";

        return (
            <>
                {formatAmount(Math.abs(numericDifference))}{" "}
                {numericDifference < 0 && "\u25BC"}
                {numericDifference > 0 && "\u25B2"}
                {differencePercent !== null && differencePercent !== undefined && (
                    <span className="ml-1 text-[10px]">({Number(differencePercent).toFixed(2)}%)</span>
                )}
            </>
        );
    };

    const sectionIds = useMemo(
        () => (rfqData?.providingFixingDetails || []).map((section, index) => String(section?._id || index)),
        [rfqData?.providingFixingDetails]
    );

    useEffect(() => {
        setCollapsedSections(prev => {
            const next = { ...prev };
            sectionIds.forEach(sectionId => {
                if (next[sectionId] === undefined) next[sectionId] = false;
            });
            return next;
        });
    }, [sectionIds]);

    const findRfqSection = (section) =>
        rfqData?.providingFixingDetails?.find(
            item => String(item?._id) === String(section?.id) || item?.sectionName === section?.sectionName
        );

    const findRfqScope = (section, scope) =>
        findRfqSection(section)?.scopes?.find(
            item => String(item?._id) === String(scope?.id) || item?.scopeOfWork === scope?.scopeOfWork
        );

    const findVendorSection = (supplier, sectionId) =>
        supplier?.vendorQuotations?.quotationSets?.slice(-1)[0]?.contractorDetails?.find(
            section => String(section?.id) === String(sectionId)
        );

    const findFirstVendorSection = (supplier, sectionId) =>
        supplier?.vendorQuotations?.quotationSets?.[0]?.contractorDetails?.find(
            section => String(section?.id) === String(sectionId)
        );

    const findVendorScope = (supplier, sectionId, scopeId) =>
        findVendorSection(supplier, sectionId)?.scopes?.find(scope => String(scope?.id) === String(scopeId));

    const findFirstVendorScope = (supplier, sectionId, scopeId) =>
        findFirstVendorSection(supplier, sectionId)?.scopes?.find(scope => String(scope?.id) === String(scopeId));

    const getMaterialKey = (material) =>
        String(material?._id || material?.id || material?.name || "");

    const findVendorMaterial = (vendorScope, rfqMaterial) => {
        const rfqMaterialKey = getMaterialKey(rfqMaterial);
        return vendorScope?.materials?.find(material =>
            getMaterialKey(material) === rfqMaterialKey ||
            String(material?.name || "").trim().toLowerCase() === String(rfqMaterial?.name || "").trim().toLowerCase()
        );
    };

    const findSectionInQuotationSet = (quotationSet, sectionId) =>
        quotationSet?.contractorDetails?.find(section => String(section?.id) === String(sectionId));

    const findScopeInSection = (vendorSection, scopeId) =>
        vendorSection?.scopes?.find(scope => String(scope?.id) === String(scopeId));

    const getRevisionRemarkText = (value) =>
        String(value || "")
            .replace(/<[^>]*>/g, "")
            .replace(/&nbsp;/gi, " ")
            .trim();

    const getContractorRemarkValue = (item) =>
        item?.comments ?? item?.remarks ?? item?.remark;

    const getContractorMaterialSignature = (material) =>
        JSON.stringify({
            basePrice: toNumber(material?.basePrice),
            quantity: toNumber(material?.quantity),
            gst: toNumber(material?.gst),
            totalExclGst: toNumber(material?.totalExclGst),
            totalWithGst: toNumber(material?.totalWithGst),
            remark: getRevisionRemarkText(getContractorRemarkValue(material)),
        });

    const getContractorScopeSignature = (scope, includeMaterials = false) =>
        JSON.stringify({
            basePrice: toNumber(scope?.basePrice),
            installationRate: toNumber(scope?.installationRate),
            quantity: toNumber(scope?.quantity),
            gst: toNumber(scope?.gst),
            totalExclGst: toNumber(scope?.totalExclGst),
            totalAmount: toNumber(scope?.totalAmount ?? scope?.totalWithGst),
            selectedQuantity: toNumber(scope?.selectedQuantity),
            selectedScopeRate: toNumber(scope?.selectedScopeRate),
            selectedSupplierPrice: toNumber(scope?.selectedSupplierPrice),
            remark: getRevisionRemarkText(getContractorRemarkValue(scope)),
            materials: includeMaterials
                ? (scope?.materials || []).map(material => getContractorMaterialSignature(material))
                : [],
        });

    const getContractorSectionSignature = (section) =>
        JSON.stringify({
            sectionTotalExclGst: toNumber(section?.sectionTotalExclGst),
            scopes: (section?.scopes || []).map(scope => getContractorScopeSignature(scope, true)),
        });

    const describeContractorSectionChanges = (previousSection, currentSection) => {
        if (!previousSection) return "Initial quote";

        const changedItems = [];
        (currentSection?.scopes || []).forEach((scope, scopeIndex) => {
            const previousScope = findScopeInSection(previousSection, scope?.id);
            const scopeLabel = plainText(scope?.scopeOfWork, `Scope ${scopeIndex + 1}`);

            if (!previousScope) {
                changedItems.push(scopeLabel);
                return;
            }

            if (hasScopeMaterials(scope)) {
                (scope?.materials || []).forEach((material) => {
                    const previousMaterial = findVendorMaterial(previousScope, material);
                    if (getContractorMaterialSignature(previousMaterial) !== getContractorMaterialSignature(material)) {
                        changedItems.push(`${scopeLabel} / ${plainText(material?.name, "Material")}`);
                    }
                });
            } else if (getContractorScopeSignature(previousScope) !== getContractorScopeSignature(scope)) {
                changedItems.push(scopeLabel);
            }
        });

        return changedItems.length ? changedItems.join(", ") : "Section total updated";
    };

    const getChangedRevisionTrail = (supplier, getSnapshot, getSignature, buildRevision) => {
        let previousSignature = null;

        return (supplier?.vendorQuotations?.quotationSets || [])
            .reduce((trail, quotationSet) => {
                const snapshot = getSnapshot(quotationSet);
                if (!snapshot) return trail;

                const signature = getSignature(snapshot);
                if (trail.length > 0 && signature === previousSignature) return trail;

                const revisionIndex = trail.length;
                trail.push(buildRevision(snapshot, revisionIndex, quotationSet, trail[trail.length - 1]?.snapshot));
                previousSignature = signature;

                return trail;
            }, [])
            .map((revision, revisionIndex, trail) => {
                const previousRevision = trail[revisionIndex - 1];
                const totalDifference = previousRevision
                    ? revision.total - previousRevision.total
                    : null;
                const totalDifferencePercent = previousRevision && previousRevision.total !== 0
                    ? (Math.abs(totalDifference) / Math.abs(previousRevision.total)) * 100
                    : null;

                return {
                    ...revision,
                    label: `R${revisionIndex}${revisionIndex === trail.length - 1 ? " (current)" : ""}`,
                    totalDifference,
                    totalDifferencePercent,
                };
            });
    };

    const getSectionRevisionTrail = (supplier, sectionId) =>
        getChangedRevisionTrail(
            supplier,
            quotationSet => findSectionInQuotationSet(quotationSet, sectionId),
            getContractorSectionSignature,
            (section, revisionIndex, quotationSet, previousSection) => ({
                snapshot: section,
                label: `R${revisionIndex}`,
                date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                total: toNumber(section?.sectionTotalExclGst),
                changedIn: describeContractorSectionChanges(previousSection, section),
            })
        );

    const getScopeRevisionTrail = (supplier, sectionId, scopeId, includeMaterials = false) =>
        getChangedRevisionTrail(
            supplier,
            quotationSet => findScopeInSection(findSectionInQuotationSet(quotationSet, sectionId), scopeId),
            scope => getContractorScopeSignature(scope, includeMaterials),
            (scope, revisionIndex, quotationSet) => ({
                snapshot: scope,
                label: `R${revisionIndex}`,
                date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                materialRate: toNumber(scope?.basePrice),
                installationRate: toNumber(scope?.installationRate),
                quantity: toNumber(scope?.quantity),
                gst: toNumber(scope?.gst),
                total: toNumber(scope?.totalExclGst),
                remark: getRevisionRemarkText(getContractorRemarkValue(scope)),
            })
        );

    const getMaterialRevisionTrail = (supplier, sectionId, scopeId, rfqMaterial) =>
        getChangedRevisionTrail(
            supplier,
            quotationSet => findVendorMaterial(
                findScopeInSection(findSectionInQuotationSet(quotationSet, sectionId), scopeId),
                rfqMaterial
            ),
            getContractorMaterialSignature,
            (material, revisionIndex, quotationSet) => ({
                snapshot: material,
                label: `R${revisionIndex}`,
                date: formatRevisionDate(quotationSet?.submittedAt || quotationSet?.updatedAt || quotationSet?.createdAt),
                submittedBy: quotationSet?.submittedBy || quotationSet?.updatedBy || "-",
                materialRate: toNumber(material?.basePrice),
                installationRate: toNumber(material?.installationRate),
                quantity: toNumber(material?.quantity),
                gst: toNumber(material?.gst),
                total: toNumber(material?.totalExclGst),
                remark: getRevisionRemarkText(getContractorRemarkValue(material)),
            })
        );

    const getRevisionKey = (type, rowKey, vendorIndex) => `${type}-${rowKey}-${vendorIndex}`;

    const toggleRevisionTrail = (event, type, rowKey, vendorIndex, trailLength) => {
        event.stopPropagation();
        if (trailLength <= 1) return;

        const key = getRevisionKey(type, rowKey, vendorIndex);
        setOpenRevisionTrail(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const renderRevisionButton = (type, rowKey, vendorIndex, trailLength) => {
        if (trailLength <= 1) return null;

        const key = getRevisionKey(type, rowKey, vendorIndex);
        const isOpen = openRevisionTrail.has(key);
        const revisionCount = Math.max(trailLength - 1, 0);

        return (
            <button
                type="button"
                className="mt-[2px] inline-flex items-center justify-center whitespace-nowrap rounded-full border border-blue-200 bg-blue-50 px-2 py-[1px] text-[10px] font-semibold leading-4 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                onClick={(event) => toggleRevisionTrail(event, type, rowKey, vendorIndex, trailLength)}
            >
                {isOpen ? "Hide revisions" : `${revisionCount} ${revisionCount === 1 ? "revision" : "revisions"}`}
            </button>
        );
    };

    const renderContractorRevisionTrail = (trail, type = "item") => (
        <div
            className="mt-2 w-[calc(100%_+_24px)] min-w-[520px] rounded-xl border border-blue-100 bg-[#F8FAFC] px-3 py-3 text-left text-[11px] text-[#3F4B64] shadow-lg"
            onClick={(event) => event.stopPropagation()}
        >
            {type === "section" ? (
                <>
                    <div className="grid grid-cols-[38px_108px_92px_74px_minmax(160px,1fr)] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                        <span>Round</span>
                        <span className="text-center">Date</span>
                        <span className="text-right">Section total</span>
                        <span className="text-right">Difference</span>
                        <span>Changed in</span>
                    </div>
                    {trail.map((revision, revisionIndex) => (
                        <div
                            key={`${revision.label}-${revisionIndex}`}
                            className="grid grid-cols-[38px_108px_92px_74px_minmax(160px,1fr)] gap-x-2 border-b border-gray-300 py-2 last:border-b-0"
                        >
                            <span className={`font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>{revision.label}</span>
                            <span className="whitespace-normal break-words text-center leading-4">
                                <span className="block">{revision.submittedBy}</span>
                                <span className="block">{revision.date}</span>
                            </span>
                            <span className={`text-right font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                {formatAmount(revision.total)}
                            </span>
                            <span className={`text-right font-semibold ${revision.totalDifference < 0 ? "text-emerald-600" : revision.totalDifference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                {formatRevisionDifference(revision.totalDifference, revision.totalDifferencePercent)}
                            </span>
                            <span className="whitespace-normal break-words leading-4 text-gray-600">{revision.changedIn || "-"}</span>
                        </div>
                    ))}
                </>
            ) : (
                <>
                    <div className="grid grid-cols-[38px_108px_78px_86px_54px_74px_82px] gap-x-2 border-b border-gray-400 pb-2 text-[11px] font-semibold text-gray-500">
                        <span>Round</span>
                        <span className="text-center">Date</span>
                        <span className="text-right">Material rate</span>
                        <span className="text-right">Installation rate</span>
                        <span className="text-right">Qty</span>
                        <span className="text-right">Difference</span>
                        <span className="text-right">Total</span>
                    </div>
                    {trail.map((revision, revisionIndex) => (
                        <React.Fragment key={`${revision.label}-${revisionIndex}`}>
                            <div
                                className={`grid grid-cols-[38px_108px_78px_86px_54px_74px_82px] gap-x-2 border-b ${revision.remark ? "border-gray-200" : "border-gray-300"} py-2`}
                            >
                                <span className={`font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-gray-600"}`}>{revision.label}</span>
                                <span className="whitespace-normal break-words text-center leading-4">
                                    <span className="block">{revision.submittedBy}</span>
                                    <span className="block">{revision.date}</span>
                                </span>
                                <span className="text-right font-medium text-[#2D2D11]">{formatAmount(revision.materialRate)}</span>
                                <span className="text-right font-medium text-[#2D2D11]">{formatAmount(revision.installationRate)}</span>
                                <span className="text-right font-medium text-[#2D2D11]">{formatQuantity(revision.quantity)}</span>
                                <span className={`text-right font-semibold ${revision.totalDifference < 0 ? "text-emerald-600" : revision.totalDifference > 0 ? "text-red-600" : "text-[#777777]"}`}>
                                    {formatRevisionDifference(revision.totalDifference, revision.totalDifferencePercent)}
                                </span>
                                <span className={`text-right font-semibold ${revisionIndex === trail.length - 1 ? "text-blue-700" : "text-[#2D2D11]"}`}>
                                    {formatAmount(revision.total)}
                                </span>
                            </div>
                            {revision.remark && (
                                <div className="border-b border-gray-300 py-1 text-gray-600">
                                    <span className="font-semibold">Remarks:</span>{" "}{revision.remark}
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </>
            )}
        </div>
    );

    const getSelectedEntry = (supplierId, scopeId) =>
        selectedMaterialsMap?.[supplierId]?.find(item => String(item?.scopeId) === String(scopeId));

    const getSelectedQuantityForScope = (scopeId, excludeSupplierId = null, sourceMap = selectedMaterialsMap) =>
        Object.entries(sourceMap || {}).reduce((sum, [supplierId, scopes]) => {
            if (excludeSupplierId && String(supplierId) === String(excludeSupplierId)) return sum;
            const selectedScope = (scopes || []).find(item => String(item?.scopeId) === String(scopeId));
            return sum + toNumber(selectedScope?.selectedQuantity);
        }, 0);

    const getLowestSectionTotal = (section) => {
        const totals = sortedSuppliers.map((supplier) => {
            const matchingSection = findVendorSection(supplier, section?._id);
            return matchingSection?.sectionTotalExclGst ?? Infinity;
        });
        return Math.min(...totals);
    };

    const getLowestScopeTotal = (sectionId, scopeId) => {
        const totals = sortedSuppliers.map((supplier) => {
            const matchingScope = findVendorScope(supplier, sectionId, scopeId);
            return matchingScope?.totalExclGst ?? Infinity;
        });
        return Math.min(...totals);
    };

    const getLowestMaterialTotal = (sectionId, scopeId, rfqMaterial) => {
        const totals = sortedSuppliers.map((supplier) => {
            const matchingScope = findVendorScope(supplier, sectionId, scopeId);
            const matchingMaterial = findVendorMaterial(matchingScope, rfqMaterial);
            return matchingMaterial?.totalExclGst ?? Infinity;
        });
        return Math.min(...totals);
    };

    const buildScopeSelectionEntry = (section, rfqScope, vendorScope, selectedQuantity) => {
        const isMaterialBacked = hasScopeMaterials(rfqScope) || hasScopeMaterials(vendorScope);
        const quantity = isMaterialBacked
            ? toNumber(rfqScope?.quantity || vendorScope?.quantity || 1, 1)
            : toNumber(selectedQuantity);
        const roundedQuantity = roundQuantity(quantity);
        const selectedScopeRate = isMaterialBacked
            ? toNumber(vendorScope?.totalExclGst)
            : getScopeUnitRate(vendorScope);

        return {
            sectionId: section?._id || section?.id,
            sectionName: section?.sectionName,
            scopeId: rfqScope?._id || vendorScope?.id,
            scopeOfWork: rfqScope?.scopeOfWork || vendorScope?.scopeOfWork,
            quantity: rfqScope?.quantity ?? vendorScope?.quantity,
            selectedQuantity: roundedQuantity,
            selectedScopeRate,
            selectedSupplierPrice: isMaterialBacked ? toNumber(vendorScope?.totalExclGst) : selectedScopeRate * roundedQuantity,
            isMaterialBacked
        };
    };

    useEffect(() => {
        if (!selectedRatesTab || rfqData?.rfqType !== "contractor") return;

        const restoredMap = {};
        sortedSuppliers?.forEach((supplier) => {
            const latestSet = supplier?.vendorQuotations?.quotationSets?.slice(-1)[0];
            latestSet?.contractorDetails?.forEach((section) => {
                const rfqSection = findRfqSection(section);
                section?.scopes?.forEach((scope) => {
                    if (!scope?.isSelected) return;
                    if (!restoredMap[supplier.supplierId]) restoredMap[supplier.supplierId] = [];
                    const rfqScope = findRfqScope(section, scope);
                    restoredMap[supplier.supplierId].push({
                        sectionId: rfqSection?._id || section.id,
                        sectionName: section.sectionName,
                        scopeId: rfqScope?._id || scope.id,
                        scopeOfWork: scope.scopeOfWork,
                        quantity: rfqScope?.quantity ?? scope.quantity,
                        selectedQuantity: scope.selectedQuantity ?? rfqScope?.quantity ?? scope.quantity,
                        selectedScopeRate: scope.selectedScopeRate ?? getScopeUnitRate(scope),
                        selectedSupplierPrice: scope.selectedSupplierPrice ?? scope.totalExclGst,
                        isMaterialBacked: hasScopeMaterials(scope) || hasScopeMaterials(rfqScope)
                    });
                });
            });
        });

        setSelectedMaterialsMap(restoredMap);
        setSelectedVendors?.(Object.keys(restoredMap));
    }, [selectedRatesTab, rfqData?.rfqType, sortedSuppliers]);

    const updateSelectedVendors = (nextMap) => {
        setSelectedVendors?.(Object.keys(nextMap || {}));
    };

    const toggleSection = (sectionId) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const toggleMaterialScope = (scopeKey) => {
        setExpandedMaterialScopes(prev => ({
            ...prev,
            [scopeKey]: !prev[scopeKey]
        }));
    };

    const handleScopeSelectionChange = (supplier, section, rfqScope, vendorScope, scopeReference) => {
        if (!canSelectRates || isEditRatesLocked || !vendorScope) return;

        const supplierId = supplier.supplierId;
        const scopeId = rfqScope?._id || vendorScope?.id;
        const isMaterialBacked = hasScopeMaterials(rfqScope) || hasScopeMaterials(vendorScope);

        setSelectedMaterialsMap(prev => {
            const updated = Object.entries(prev || {}).reduce((acc, [id, scopes]) => {
                acc[id] = [...(scopes || [])];
                return acc;
            }, {});
            const currentSupplierScopes = updated[supplierId] || [];
            const existingSelection = currentSupplierScopes.find(item => String(item.scopeId) === String(scopeId));

            if (existingSelection) {
                updated[supplierId] = currentSupplierScopes.filter(item => String(item.scopeId) !== String(scopeId));
                if (updated[supplierId].length === 0) delete updated[supplierId];
                updateSelectedVendors(updated);
                return updated;
            }

            if (isMaterialBacked) {
                Object.keys(updated).forEach((vendorId) => {
                    updated[vendorId] = (updated[vendorId] || []).filter(item => String(item.scopeId) !== String(scopeId));
                    if (updated[vendorId].length === 0) delete updated[vendorId];
                });
            }

            const remainingQuantity = roundQuantity(Math.max(
                toNumber(rfqScope?.quantity || vendorScope?.quantity) - getSelectedQuantityForScope(scopeId, supplierId, updated),
                0
            ));
            const selectedQuantity = isMaterialBacked
                ? toNumber(rfqScope?.quantity || vendorScope?.quantity || 1, 1)
                : remainingQuantity;

            if (!isMaterialBacked && selectedQuantity <= 0) {
                enqueueSnackbar(`Full RFQ quantity is already allocated for scope ${scopeReference || plainText(rfqScope?.scopeOfWork, "scope")}.`, {
                    variant: "warning",
                    autoHideDuration: 4000,
                });
                return prev;
            }

            if (!updated[supplierId]) updated[supplierId] = [];
            updated[supplierId].push(buildScopeSelectionEntry(section, rfqScope, vendorScope, selectedQuantity));
            updateSelectedVendors(updated);
            return updated;
        });
    };

    const handleScopeQuantityChange = (supplierId, rfqScope, vendorScope, value) => {
        const normalizedValue = normalizeDecimalInput(value);
        const scopeId = rfqScope?._id || vendorScope?.id;

        setSelectedMaterialsMap(prev => {
            const updated = Object.entries(prev || {}).reduce((acc, [id, scopes]) => {
                acc[id] = [...(scopes || [])];
                return acc;
            }, {});
            const entry = updated[supplierId]?.find(item => String(item.scopeId) === String(scopeId));
            if (!entry) return prev;

            const selectedQuantity = toNumber(normalizedValue);
            const selectedScopeRate = getScopeUnitRate(vendorScope);
            entry.selectedQuantity = normalizedValue;
            entry.selectedScopeRate = selectedScopeRate;
            entry.selectedSupplierPrice = selectedScopeRate * selectedQuantity;

            return updated;
        });
    };

    const renderScopeSelection = () => (
        rfqData?.providingFixingDetails?.flatMap((section, sectionIndex) => {
            const sectionKey = String(section?._id || sectionIndex);
            const sectionLineItemKey = getLineItemKey?.("contractor-section", section, sectionIndex)
                ?? `contractor-section:${section?._id ?? section?.id ?? sectionIndex}`;
            if (hiddenLineItemKeys.has(sectionLineItemKey)) return [];

            const sectionLineItemLabel = plainText(section?.sectionName, `Section ${sectionIndex + 1}`);
            const lowestSectionTotal = getLowestSectionTotal(section);
            const isCollapsed = Boolean(collapsedSections[sectionKey]);
            const leftItemSpan = hasBudgetAmountColumn ? 8 : 12;
            const leftQtySpan = 3;
            const leftBudgetSpan = 4;
            const leftLowestSpan = 4;
            const vendorGridClass = canSelectRates
                ? (isSelectedValueVisible
                    ? "grid-cols-[1.25fr_0.75fr_1fr_1fr_1fr]"
                    : "grid-cols-[1.25fr_0.75fr_1fr_1fr]")
                : "grid-cols-3";
            const sectionHeader = (
                <tr key={`section-${sectionKey}`} className="bg-[#F7F7F0]">
                    <td className="comparison-sticky-rfq-cell sticky left-0 z-10 border border-gray-300 bg-[#F7F7F0]" style={{ minWidth: 560, maxWidth: 560, width: 560, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                        <div className="grid grid-cols-20 gap-x-2 px-[22px] py-2 text-xs">
                            <button
                                type="button"
                                onClick={() => toggleSection(sectionKey)}
                                className="col-span-1 text-left text-sm font-semibold text-[#4B4B1C]"
                            >
                                {isCollapsed ? "▸" : "▾"}
                            </button>
                            <div
                                className="flex min-w-0 items-center gap-1"
                                style={{ gridColumn: `span ${leftItemSpan} / span ${leftItemSpan}` }}
                            >
                                {renderLineItemHideButton?.(sectionLineItemKey, sectionLineItemLabel)}
                                <p className="shrink-0">{sectionIndex + 1}.</p>
                                <div className="min-w-0 break-words whitespace-normal font-semibold leading-4 text-blue-500 underline">
                                    {ReactHtmlParser(section?.sectionName)}
                                </div>
                            </div>
                            <p
                                className="text-right text-xs text-gray-500"
                                style={{ gridColumn: `span ${leftQtySpan} / span ${leftQtySpan}` }}
                            >
                                {section?.scopes?.length || 0} scopes
                            </p>
                            {hasBudgetAmountColumn && (
                                <p
                                    className="text-right text-xs"
                                    style={{ gridColumn: `span ${leftBudgetSpan} / span ${leftBudgetSpan}` }}
                                >
                                    {formatAmount(section?.budgetAmount)}
                                </p>
                            )}
                            <p
                                className="text-right text-xs font-semibold text-blue-700"
                                style={{ gridColumn: `span ${leftLowestSpan} / span ${leftLowestSpan}` }}
                            >
                                {formatAmount(lowestSectionTotal === Infinity ? 0 : lowestSectionTotal)}
                            </p>
                        </div>
                    </td>
                    {sortedSuppliers?.map((supplier, vendorIndex) => {
                        const vendorSection = findVendorSection(supplier, section?._id);
                        const firstSection = findFirstVendorSection(supplier, section?._id);
                        const firstRate = toNumber(firstSection?.sectionTotalExclGst);
                        const currentRate = toNumber(vendorSection?.sectionTotalExclGst);
                        const diff = firstRate - currentRate;
                        const safeDiff = Number.isFinite(Number(diff)) && Math.abs(Number(diff)) > 0.01 ? Number(diff) : 0;
                        const revisionTrail = getSectionRevisionTrail(supplier, section?._id);
                        const isRevisionTrailOpen = openRevisionTrail.has(getRevisionKey("section", sectionKey, vendorIndex));

                        if (!canSelectRates) {
                            return (
                                <td key={supplier.supplierId} className="min-w-[680px] max-w-[680px] border border-gray-300 bg-[#F7F7F0] px-4 py-2">
                                    <div className={`grid ${vendorGridClass} gap-x-3 text-xs`}>
                                        <p className="text-right text-[#777777]">{formatAmount(firstRate)}</p>
                                        <div className="flex flex-col items-end">
                                            <p className="w-fit text-right font-semibold">
                                                {formatAmount(currentRate)}
                                            </p>
                                            {renderRevisionButton("section", sectionKey, vendorIndex, revisionTrail.length)}
                                        </div>
                                        {renderDifferenceValue(safeDiff, firstRate)}
                                    </div>
                                    {isRevisionTrailOpen && renderContractorRevisionTrail(revisionTrail, "section")}
                                </td>
                            );
                        }

                        return (
                            <td key={supplier.supplierId} className="min-w-[680px] max-w-[680px] border border-gray-300 bg-[#F7F7F0] px-4 py-2">
                                <div className={`grid ${vendorGridClass} gap-x-3 text-xs`}>
                                    <p className="text-left font-semibold">{formatAmount(vendorSection?.sectionTotalExclGst)}</p>
                                    <p className="text-center text-[#777777]">-</p>
                                    <p className="text-right text-[#777777]">Section</p>
                                    <p className="text-right">-</p>
                                    {isSelectedValueVisible && <p className="text-right">-</p>}
                                </div>
                            </td>
                        );
                    })}
                </tr>
            );

            const scopeRows = isCollapsed ? [] : (section?.scopes || []).flatMap((scope, scopeIndex) => {
                const scopeId = scope?._id;
                const scopeKey = `${sectionKey}-${scopeId || scopeIndex}`;
                const lineItemKey = getLineItemKey?.("contractor", scope, `${sectionIndex}-${scopeIndex}`) ?? `contractor:${scope?._id ?? scope?.id ?? `${sectionIndex}-${scopeIndex}`}`;
                if (hiddenLineItemKeys.has(lineItemKey)) return [];
                const lineItemLabel = plainText(scope?.scopeOfWork, `Scope ${sectionIndex + 1}.${scopeIndex + 1}`);
                const scopeIsMaterialBacked = hasScopeMaterials(scope);
                const materialsExpanded = Boolean(expandedMaterialScopes[scopeKey]);
                const selectedQuantityForScope = getSelectedQuantityForScope(scopeId);
                const isQuantityExceeded = !scopeIsMaterialBacked && selectedQuantityForScope > toNumber(scope?.quantity);
                const lowestScopeTotal = getLowestScopeTotal(section?._id, scopeId);
                const hasAnyScopeRemark = sortedSuppliers?.some((supplier) => {
                    const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                    return Boolean(plainText(getContractorRemarkValue(vendorScope), ""));
                });

                const scopeRow = (
                    <tr key={scopeKey} className="bg-white">
                        <td className="comparison-sticky-rfq-cell sticky left-0 z-10 border border-gray-300 bg-white" style={{ minWidth: 560, maxWidth: 560, width: 560, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                            <div className="grid grid-cols-20 gap-x-2 px-[22px] py-2 text-xs">
                                <div className="col-span-1 flex flex-col items-center gap-1">
                                    <p>{sectionIndex + 1}.{scopeIndex + 1}</p>
                                    {renderLineItemHideButton?.(lineItemKey, lineItemLabel)}
                                </div>
                                <div
                                    className="min-w-0"
                                    style={{ gridColumn: `span ${leftItemSpan} / span ${leftItemSpan}` }}
                                >
                                        <div className="min-w-0 break-words whitespace-normal leading-4">{ReactHtmlParser(scope?.scopeOfWork)}</div>
                                        {scopeIsMaterialBacked && (
                                            <button
                                                type="button"
                                                onClick={() => toggleMaterialScope(scopeKey)}
                                                className="mt-1 text-left text-[11px] font-medium text-blue-600 underline"
                                            >
                                                {materialsExpanded ? "Hide materials" : `View materials (${scope?.materials?.length || 0})`}
                                            </button>
                                        )}
                                </div>
                                <p
                                    className="text-right text-xs"
                                    style={{ gridColumn: `span ${leftQtySpan} / span ${leftQtySpan}` }}
                                >
                                    {scopeIsMaterialBacked
                                        ? "Full"
                                        : `${formatQuantity(scope?.quantity || 0)} ${getFormattedUnit(scope?.unit) || ""}`}
                                </p>
                                {hasBudgetAmountColumn && (
                                    <p
                                        className="text-right text-xs"
                                        style={{ gridColumn: `span ${leftBudgetSpan} / span ${leftBudgetSpan}` }}
                                    >
                                        {formatAmount(scope?.budgetAmount)}
                                    </p>
                                )}
                                <p
                                    className="text-right text-xs text-blue-700"
                                    style={{ gridColumn: `span ${leftLowestSpan} / span ${leftLowestSpan}` }}
                                >
                                    {formatAmount(lowestScopeTotal === Infinity ? 0 : lowestScopeTotal)}
                                </p>
                            </div>
                        </td>
                        {sortedSuppliers?.map((supplier, vendorIndex) => {
                            const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                            const firstVendorScope = findFirstVendorScope(supplier, section?._id, scopeId);
                            const selectedEntry = getSelectedEntry(supplier.supplierId, scopeId);
                            const isSelected = Boolean(selectedEntry);
                            const currentRate = toNumber(vendorScope?.totalExclGst);
                            const firstRate = toNumber(firstVendorScope?.totalExclGst);
                            const selectedValue = selectedEntry?.selectedSupplierPrice ?? currentRate;
                            const diff = firstRate - currentRate;
                            const safeDiff = Number.isFinite(Number(diff)) && Math.abs(Number(diff)) > 0.01 ? Number(diff) : 0;
                            const isLowest = currentRate > 0 && currentRate === lowestScopeTotal;
                            const revisionTrail = scopeIsMaterialBacked
                                ? []
                                : getScopeRevisionTrail(supplier, section?._id, scopeId, false);
                            const isRevisionTrailOpen = openRevisionTrail.has(getRevisionKey("scope", scopeKey, vendorIndex));

                            return (
                                <td key={supplier.supplierId} className="min-w-[680px] max-w-[680px] border border-gray-300 px-4 py-2">
                                    {!canSelectRates ? (
                                        <>
                                            <div className={`grid ${vendorGridClass} items-center gap-x-3 text-xs`}>
                                                <p className="text-right text-[#777777]">{formatAmount(firstRate)}</p>
                                                <div className="flex flex-col items-end">
                                                    <p className={`w-fit text-right ${isLowest ? "bg-lime-200 px-1 rounded font-medium" : "text-[#777777]"}`}>
                                                        {formatAmount(currentRate)}
                                                    </p>
                                                    {renderRevisionButton("scope", scopeKey, vendorIndex, revisionTrail.length)}
                                                </div>
                                                {renderDifferenceValue(safeDiff, firstRate)}
                                            </div>
                                            {isRevisionTrailOpen && renderContractorRevisionTrail(revisionTrail)}
                                        </>
                                    ) : (
                                        <>
                                    <div className={`grid ${vendorGridClass} items-center gap-x-3 text-xs`}>
                                        <div className="flex items-center gap-1 text-left">
                                            {canSelectRates && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => handleScopeSelectionChange(supplier, section, scope, vendorScope, `${sectionIndex + 1}.${scopeIndex + 1}`)}
                                                    disabled={isEditRatesLocked || !vendorScope}
                                                    className="mt-[2px] h-3 w-3 cursor-pointer accent-[#DC7604] focus:ring-0"
                                                />
                                            )}
                                            <div className="flex flex-col items-start">
                                                <p className={`${isLowest ? "bg-lime-200 px-1 rounded font-medium" : "text-[#777777]"}`}>
                                                    {formatAmount(currentRate)}
                                                </p>
                                                {renderRevisionButton("scope", scopeKey, vendorIndex, revisionTrail.length)}
                                            </div>
                                        </div>

                                        <div className="text-center">
                                            {scopeIsMaterialBacked ? (
                                                <p>{isSelected ? "Full" : "-"}</p>
                                            ) : canSelectRates && isSelected ? (
                                                <div className="flex flex-col items-center gap-1">
                                                    <input
                                                        type="text"
                                                        value={formatQuantityInputValue(selectedEntry?.selectedQuantity)}
                                                        onChange={(event) => handleScopeQuantityChange(supplier.supplierId, scope, vendorScope, event.target.value)}
                                                        className={`w-[76px] rounded-md border px-1 py-0 text-right text-xs ${isQuantityExceeded ? "border-red-500 bg-red-50" : "border-gray-300"}`}
                                                    />
                                                    <p className="text-[10px] text-gray-500">
                                                        {formatQuantity(selectedEntry?.selectedQuantity || 0)}/{formatQuantity(scope?.quantity || 0)}
                                                    </p>
                                                </div>
                                            ) : (
                                                <p>-</p>
                                            )}
                                        </div>

                                        <p className="text-right text-[#777777]">{formatAmount(firstRate)}</p>
                                        {renderDifferenceValue(safeDiff, firstRate)}
                                        {isSelectedValueVisible && (
                                            <p className="text-right text-[#2D2D11]">
                                                {isSelected ? formatAmount(selectedValue) : "-"}
                                            </p>
                                        )}
                                    </div>
                                    {isRevisionTrailOpen && renderContractorRevisionTrail(revisionTrail)}
                                        </>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                );

                const scopeRemarkRow = hasAnyScopeRemark ? (
                    <tr key={`${scopeKey}-remarks`} className="bg-white text-xs">
                        <td className="comparison-sticky-rfq-cell sticky left-0 z-10 border border-gray-300 bg-white px-[22px] py-2" style={{ minWidth: 560, maxWidth: 560, width: 560, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                            <p className="text-[11px] font-semibold italic text-[#6B7280]">Comments / Methodology</p>
                        </td>
                        {sortedSuppliers?.map((supplier, vendorIndex) => {
                            const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                            const remarkValue = getContractorRemarkValue(vendorScope);
                            const remark = plainText(remarkValue, "");

                            return (
                                <td
                                    key={`scope-remark-${scopeKey}-${vendorIndex}`}
                                    className="min-w-[680px] max-w-[680px] border border-gray-300 px-4 py-2"
                                >
                                    {remark ? (
                                        <p className="whitespace-normal break-words text-[11px] italic leading-4 text-[#555555]">
                                            {ReactHtmlParser(remarkValue)}
                                        </p>
                                    ) : (
                                        <span className="text-[11px] text-gray-300">-</span>
                                    )}
                                </td>
                            );
                        })}
                    </tr>
                ) : null;

                const materialRows = scopeIsMaterialBacked && materialsExpanded
                    ? (scope?.materials || []).flatMap((material, materialIndex) => {
                        const materialKey = getMaterialKey(material) || materialIndex;
                        const materialRowKey = `${scopeKey}-material-${materialKey}`;
                        const lowestMaterialTotal = getLowestMaterialTotal(section?._id, scopeId, material);
                        const hasAnyMaterialRemark = sortedSuppliers?.some((supplier) => {
                            const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                            const vendorMaterial = findVendorMaterial(vendorScope, material);
                            return Boolean(plainText(getContractorRemarkValue(vendorMaterial), ""));
                        });

                        const materialRow = (
                            <tr key={materialRowKey} className="border-y border-gray-300 bg-[#FCFCFA]">
                                <td className="comparison-sticky-rfq-cell sticky left-0 z-10 border-y border-r border-gray-300 bg-[#FCFCFA]" style={{ minWidth: 560, maxWidth: 560, width: 560, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                                    <div className="grid grid-cols-20 gap-x-2 border-y border-gray-300 px-[22px] py-2 text-[11px]">
                                        <div
                                            className="flex min-w-0 gap-1 pl-5 text-gray-700"
                                            style={{ gridColumn: `span ${leftItemSpan + 1} / span ${leftItemSpan + 1}` }}
                                        >
                                            <p>{sectionIndex + 1}.{scopeIndex + 1}.{materialIndex + 1}</p>
                                            <p className="min-w-0 break-words whitespace-normal leading-4">{plainText(material?.name, "-")}</p>
                                        </div>
                                        <p
                                            className="text-right text-gray-700"
                                            style={{ gridColumn: `span ${leftQtySpan} / span ${leftQtySpan}` }}
                                        >
                                            {formatQuantity(material?.quantity || 0)} {getFormattedUnit(material?.unit) || ""}
                                        </p>
                                        {hasBudgetAmountColumn && (
                                            <p
                                                className="text-right text-gray-700"
                                                style={{ gridColumn: `span ${leftBudgetSpan} / span ${leftBudgetSpan}` }}
                                            >
                                                {formatAmount(material?.budget || material?.budgetAmount)}
                                            </p>
                                        )}
                                        <p
                                            className="text-right text-blue-700"
                                            style={{ gridColumn: `span ${leftLowestSpan} / span ${leftLowestSpan}` }}
                                        >
                                            {formatAmount(lowestMaterialTotal === Infinity ? 0 : lowestMaterialTotal)}
                                        </p>
                                    </div>
                                </td>
                                {sortedSuppliers?.map((supplier, vendorIndex) => {
                                    const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                                    const firstVendorScope = findFirstVendorScope(supplier, section?._id, scopeId);
                                    const vendorMaterial = findVendorMaterial(vendorScope, material);
                                    const firstVendorMaterial = findVendorMaterial(firstVendorScope, material);
                                    const currentRate = toNumber(vendorMaterial?.totalExclGst);
                                    const firstRate = toNumber(firstVendorMaterial?.totalExclGst);
                                    const diff = firstRate - currentRate;
                                    const safeDiff = vendorMaterial && firstVendorMaterial && Number.isFinite(Number(diff)) && Math.abs(Number(diff)) > 0.01 ? Number(diff) : 0;
                                    const isLowest = currentRate > 0 && currentRate === lowestMaterialTotal;
                                    const revisionTrail = getMaterialRevisionTrail(supplier, section?._id, scopeId, material);
                                    const isRevisionTrailOpen = openRevisionTrail.has(getRevisionKey("material", materialRowKey, vendorIndex));

                                    return (
                                        <td key={supplier.supplierId} className="min-w-[680px] max-w-[680px] border border-gray-300 bg-[#FCFCFA] px-4 py-2">
                                            {!canSelectRates ? (
                                                <>
                                                    <div className={`grid ${vendorGridClass} items-center gap-x-3 text-[11px]`}>
                                                        <p className="text-right text-[#777777]">{firstVendorMaterial ? formatAmount(firstRate) : "-"}</p>
                                                        <div className="flex flex-col items-end">
                                                            <p className={`w-fit text-right ${isLowest ? "bg-lime-200 px-1 rounded font-medium" : "text-[#777777]"}`}>
                                                                {vendorMaterial ? formatAmount(currentRate) : "-"}
                                                            </p>
                                                            {renderRevisionButton("material", materialRowKey, vendorIndex, revisionTrail.length)}
                                                        </div>
                                                        {renderDifferenceValue(safeDiff, firstRate)}
                                                    </div>
                                                    {isRevisionTrailOpen && renderContractorRevisionTrail(revisionTrail)}
                                                </>
                                            ) : (
                                                <>
                                            <div className={`grid ${vendorGridClass} items-center gap-x-3 text-[11px]`}>
                                                <div className="flex items-center gap-1 text-left">
                                                    <span className="inline-block h-3 w-3" />
                                                    <div className="flex flex-col items-start">
                                                        <p className={`${isLowest ? "bg-lime-200 px-1 rounded font-medium" : "text-[#777777]"}`}>
                                                            {vendorMaterial ? formatAmount(currentRate) : "-"}
                                                        </p>
                                                        {renderRevisionButton("material", materialRowKey, vendorIndex, revisionTrail.length)}
                                                    </div>
                                                </div>
                                                <p className="text-center text-[#777777]">
                                                    {vendorMaterial ? `${formatQuantity(vendorMaterial?.quantity || material?.quantity || 0)} ${getFormattedUnit(vendorMaterial?.unit || material?.unit) || ""}` : "-"}
                                                </p>
                                                <p className="text-right text-[#777777]">{firstVendorMaterial ? formatAmount(firstRate) : "-"}</p>
                                                {renderDifferenceValue(safeDiff, firstRate)}
                                                {isSelectedValueVisible && <p className="text-right text-[#777777]">-</p>}
                                            </div>
                                            {isRevisionTrailOpen && renderContractorRevisionTrail(revisionTrail)}
                                                </>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );

                        const materialRemarkRow = hasAnyMaterialRemark ? (
                            <tr key={`${materialRowKey}-remarks`} className="bg-[#FCFCFA] text-xs">
                                <td className="comparison-sticky-rfq-cell sticky left-0 z-10 border border-gray-300 bg-[#FCFCFA] px-[22px] py-2" style={{ minWidth: 560, maxWidth: 560, width: 560, boxShadow: '4px 0 4px rgba(0, 0, 0, 0.1)' }}>
                                    <p className="pl-5 text-[11px] font-semibold italic text-[#6B7280]">Comments / Methodology</p>
                                </td>
                                {sortedSuppliers?.map((supplier, vendorIndex) => {
                                    const vendorScope = findVendorScope(supplier, section?._id, scopeId);
                                    const vendorMaterial = findVendorMaterial(vendorScope, material);
                                    const remarkValue = getContractorRemarkValue(vendorMaterial);
                                    const remark = plainText(remarkValue, "");

                                    return (
                                        <td
                                            key={`material-remark-${materialRowKey}-${vendorIndex}`}
                                            className="min-w-[680px] max-w-[680px] border border-gray-300 bg-[#FCFCFA] px-4 py-2"
                                        >
                                            {remark ? (
                                                <p className="whitespace-normal break-words text-[11px] italic leading-4 text-[#555555]">
                                                    {ReactHtmlParser(remarkValue)}
                                                </p>
                                            ) : (
                                                <span className="text-[11px] text-gray-300">-</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ) : null;

                        return materialRemarkRow ? [materialRow, materialRemarkRow] : [materialRow];
                    })
                    : [];

                return [scopeRow, ...(scopeRemarkRow ? [scopeRemarkRow] : []), ...materialRows];
            });

            return [sectionHeader, ...scopeRows];
        })
    );

    return renderScopeSelection();
};

export default ContractorRFQTable;
